import posthog from 'posthog-js';
import { stripUrlSecrets } from '@reflex/shared/sentry-scrub';

/**
 * Reflex's managed PostHog reverse proxy. Ingestion and the lazily loaded
 * `/static/*.js` extension bundles (session recorder, surveys) are served
 * from a first-party Reflex domain so tracker blockers — which match on
 * `*.posthog.com` — do not silently drop a slice of our product analytics.
 */
const POSTHOG_PROXY_HOST = 'https://n.runloop.ai';

/**
 * PostHog's US cloud. Where the project actually lives, and therefore where
 * in-app links (session replays, the toolbar) have to point once ingestion
 * is proxied.
 */
const POSTHOG_CLOUD_HOST = 'https://us.posthog.com';

/**
 * Treat a blank env value as unset. Vite inlines the build-time value
 * verbatim and an undefined GitHub Actions secret arrives as `""`, which
 * `??` would otherwise pass through as a real (empty) setting.
 */
function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

const POSTHOG_KEY = nonEmpty(import.meta.env.VITE_POSTHOG_KEY);

/**
 * Ingestion endpoint. Defaults to the reverse proxy; `VITE_POSTHOG_HOST` is
 * an escape hatch for pointing a deployment straight at PostHog (or at the
 * EU cloud) without a code change.
 */
const POSTHOG_HOST = nonEmpty(import.meta.env.VITE_POSTHOG_HOST) ?? POSTHOG_PROXY_HOST;

/**
 * The `ui_host` to pair with an ingestion host.
 *
 * PostHog needs `ui_host` only when `api_host` is *not* PostHog itself:
 * behind a proxy it has no way to know which cloud region the project lives
 * in, so links it renders (`get_session_replay_url()`, the toolbar) would
 * otherwise be built from the proxy domain and 404. When ingestion already
 * points at PostHog, `null` lets the client derive the UI host from
 * `api_host` — which keeps a `VITE_POSTHOG_HOST` override aimed at the EU
 * cloud from being paired with US links.
 */
function resolveUiHost(apiHost: string): string | null {
  return isPostHogOwnedHost(apiHost) ? null : POSTHOG_CLOUD_HOST;
}

function isPostHogOwnedHost(apiHost: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(apiHost).hostname;
  } catch {
    // A malformed override is not PostHog's; pairing it with the US UI host
    // is the safer guess than deriving links from an unparseable URL.
    return false;
  }
  return hostname === 'posthog.com' || hostname.endsWith('.posthog.com');
}

// PostHog is a browser-only client. Guard `window` access so this module can
// be imported under Node test runners (e.g. plugin contract tests that
// transitively pull in `@/hooks/auth/useAuth`). Outside a browser the module
// degrades to a no-op — every exported function returns early on
// `isPostHogEnabled === false`.
const isBrowser = typeof window !== 'undefined';
const hostname = isBrowser ? window.location.hostname : '';
const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

export const isPostHogEnabled = isBrowser && !!POSTHOG_KEY && !isLocalhost;

const POSTHOG_URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$external_click_url',
] as const;

/** Redact URL-bearing analytics properties without changing unrelated event data. */
function scrubPostHogEvent<T extends { properties?: Record<string, unknown> } | null>(event: T): T {
  if (!event?.properties) return event;
  let properties: Record<string, unknown> | undefined;
  for (const key of POSTHOG_URL_PROPERTIES) {
    const value = event.properties[key];
    if (typeof value !== 'string') continue;
    const scrubbed = stripUrlSecrets(value);
    if (scrubbed === value) continue;
    properties ??= { ...event.properties };
    properties[key] = scrubbed;
  }
  return properties ? ({ ...event, properties } as T) : event;
}

export function initPostHog() {
  if (!isPostHogEnabled) return;

  posthog.init(POSTHOG_KEY!, {
    api_host: POSTHOG_HOST,
    ui_host: resolveUiHost(POSTHOG_HOST),
    // Opt into PostHog's dated default-config set instead of the legacy
    // defaults. This is also what turns on pageview capture: the set's
    // `capture_pageview: 'history_change'` hooks the History API and fires a
    // `$pageview` per SPA navigation — deduped on pathname, so nuqs
    // query-param writes do not produce extra events.
    defaults: '2026-05-30',
    person_profiles: 'identified_only',

    // Session recordings leave our trust boundary. Mask every input, not
    // just password fields: agent env-var values, blueprint build secrets,
    // and a revealed `SecretInput` are all plain text fields whose contents
    // are credentials. Pinned explicitly so a default change in posthog-js
    // fails the init test rather than silently recording them.
    session_recording: {
      maskAllInputs: true,
      // Tokens are rendered once in text nodes, not inputs. This masks every
      // text node so one-time API-key and SCIM-token displays stay private.
      maskTextSelector: '*',
      // PostHog passes replay Meta-frame and navigation URLs through this
      // callback independently of event `before_send`.
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: stripUrlSecrets(request.name),
      }),
    },
    // `$pageview` and related events carry URLs outside session-recording
    // frames, so scrub their known URL properties at the export boundary.
    before_send: scrubPostHogEvent,
  });
}

/**
 * PostHog gets opaque ids only. The person is keyed by `user.id` and carries
 * no email, login, or display name; join the id against the users table when
 * triage needs a person (see docs/internal/security/telemetry-privacy.md).
 */
export function identifyUser(user: { id: string }) {
  if (!isPostHogEnabled) return;

  posthog.identify(user.id);
}

/**
 * Attach the current organization to every subsequent event. Mirrors
 * the server-side `groups: { organization: <orgId> }` payload so PostHog
 * dashboards stay sliceable by tenant on both surfaces.
 */
export function identifyOrganization(org: { id: string; slug?: string }) {
  if (!isPostHogEnabled) return;
  // Opaque id plus slug only: the slug is what dashboards group on; the
  // display name stays out of the third-party processor.
  posthog.group('organization', org.id, {
    slug: org.slug,
  });
}

/**
 * Drop any active group memberships from the local PostHog client. Called
 * on sign-out and on switching to an empty/null organization so the next
 * sign-in does not inherit the previous tenant's group context.
 */
export function clearActiveGroups() {
  if (!isPostHogEnabled) return;
  if (typeof posthog.resetGroups === 'function') {
    posthog.resetGroups();
  }
}

export function resetPostHog() {
  if (!isPostHogEnabled) return;
  posthog.reset();
}

export { posthog };
